import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tooltip,
} from '@mui/material'
import { CreditAppLink } from '@src/components/CreditAppLink'
import { CreditAppMatchResult, isCreditAppStatusBlocked } from '@src/types/automationDocument'
import { useTranslation } from 'react-i18next'
import UploadedDocumentsList from './UploadedDocumentsList'

interface FinalizeConfirmationDialogProps {
  open: boolean
  finalizeDialogState: 'toolbar' | 'lastPage' | 'closed'
  linkedCreditApp: CreditAppMatchResult | null
  documentId: string
  isFinalizing: boolean
  pageDocumentTypeIds: number[]
  pageDocumentTypeNames: Map<number, string>
  pageCountByTypeId: Map<number, number>
  excludedDocumentTypeIds: number[]
  sentDocumentTypeIds: number[]
  onExcludedTypeIdsChange: (ids: number[]) => void
  onConfirm: () => void
  onCancel: () => void
}

export const FinalizeConfirmationDialog = ({
  open,
  finalizeDialogState,
  linkedCreditApp,
  documentId,
  isFinalizing,
  pageDocumentTypeIds,
  pageDocumentTypeNames,
  pageCountByTypeId,
  excludedDocumentTypeIds,
  sentDocumentTypeIds,
  onExcludedTypeIdsChange,
  onConfirm,
  onCancel,
}: FinalizeConfirmationDialogProps) => {
  const { t, i18n } = useTranslation()

  const isBlocked = linkedCreditApp
    ? isCreditAppStatusBlocked(linkedCreditApp.productLine, linkedCreditApp.statusId)
    : false

  const statusLabel = linkedCreditApp
    ? i18n.language === 'fr'
      ? linkedCreditApp.status
      : linkedCreditApp.statusEn
    : ''

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('common.confirmation')}
        {linkedCreditApp?.link && (
          <Tooltip title={t('common.details')}>
            <Stack>
              <CreditAppLink creditApp={linkedCreditApp} />
            </Stack>
          </Tooltip>
        )}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {finalizeDialogState === 'toolbar'
            ? t('docsAutomation.finalizeConfirmationToolbar')
            : finalizeDialogState === 'lastPage'
              ? t('docsAutomation.finalizeConfirmationLastPage')
              : ''}
        </DialogContentText>
        {linkedCreditApp && isBlocked && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('docsAutomation.cannotSendDocuments', { status: statusLabel })}
          </Alert>
        )}
        {linkedCreditApp && !isBlocked && (
          <Box sx={{ mt: 2 }}>
            <DialogContentText sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('docsAutomation.uploadedDocuments.title')}
            </DialogContentText>
            {finalizeDialogState !== 'closed' && (
              <UploadedDocumentsList
                documentId={documentId}
                filterByDocumentTypeIds={pageDocumentTypeIds}
                documentTypeNames={pageDocumentTypeNames}
                pageCountByTypeId={pageCountByTypeId}
                sentDocumentTypeIds={sentDocumentTypeIds}
                onExcludedTypeIdsChange={onExcludedTypeIdsChange}
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {!isBlocked && excludedDocumentTypeIds.length > 0 && excludedDocumentTypeIds.length === pageDocumentTypeIds.length ? (
          <Alert severity="warning" sx={{ py: 0 }}>
            {t('docsAutomation.uploadedDocuments.nothingSent')}
          </Alert>
        ) : (
          <Box />
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          <Button onClick={onConfirm} variant="contained" color="primary" disabled={isFinalizing}>
            {t('common.confirm')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
